import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ThumbsUp } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import type { ImageSourcePropType } from "react-native";
import { Image, View, XStack, YStack } from "tamagui";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { Group } from "../../ui/Group";
import type { IconProp } from "../../ui/icons";
import { Label } from "../../ui/Label";
import { BackToPlanHeader } from "../../ui/navigation/BackToPlanHeader";
import { Screen } from "../../ui/Screen";
import { useStores } from "../stores";
import { learnMoreData } from "./learndata/utils";

type Props = NativeStackScreenProps<ReactNavigation.RootParamList, "Learn">;

type LearnDescription = {
  icon?: IconProp;
  title?: string;
  muted?: boolean;
  description: string;
};

type LearnFeature = {
  icon?: IconProp;
  text: string;
};

export type LearnAboutInfo = {
  title?: string;
  titleIcon?: IconProp;
  subtitle?: string;
  image?: ImageSourcePropType;
  featureList?: LearnFeature[];
  descriptions?: LearnDescription[];
};

export const LearnBox: React.FC<LearnAboutInfo> = observer(
  ({ titleIcon, title, subtitle, image, featureList, descriptions }) => {
    const TitleIcon = titleIcon;

    return (
      <YStack gap="$largest">
        <Box>
          <YStack gap="$medium">
            {!!title && (
              <XStack gap="$small" alignItems="center">
                {TitleIcon && <TitleIcon />}
                <Label>{title}</Label>
              </XStack>
            )}

            {!!subtitle && <Body muted>{subtitle}</Body>}

            {!!image && (
              <Image
                source={image}
                flex={1}
                resizeMethod={"resize"}
                width={"100%"}
              />
            )}
          </YStack>

          {featureList && (
            <Group>
              {featureList.map((feature, i) => {
                const FIcon = feature.icon;
                return (
                  <XStack key={i} gap="$medium" alignItems="center">
                    {FIcon && (
                      <View minWidth={24}>
                        <FIcon />
                      </View>
                    )}
                    <Body>{feature.text}</Body>
                  </XStack>
                );
              })}
            </Group>
          )}
        </Box>

        {descriptions?.map((desc, i) => {
          const DIcon = desc.icon;
          return (
            <Box key={i}>
              <YStack gap="$medium">
                <XStack gap="$medium" alignItems="center">
                  {DIcon && <DIcon />}
                  <Label>{desc.title}</Label>
                </XStack>

                <Body muted={desc.muted}>{desc.description}</Body>
              </YStack>
            </Box>
          );
        })}
      </YStack>
    );
  },
);

export const LearnScreen: React.FC<Props> = observer(({ route }) => {
  const navigation = usePlanNavigation();

  const { sectionsStore, plansStore } = useStores();

  const cardType = route.params.card;
  const planUid = route.params.planUid;
  const sectionUid = route.params.sectionUid;

  const dataBoxes = learnMoreData[cardType];

  const section = sectionUid ? sectionsStore.byId.get(sectionUid) : undefined;
  const plan = plansStore.byId.get(planUid);

  React.useEffect(() => {
    plan?.setShowComments(false);

    return navigation.addListener("beforeRemove", (_e) => {
      section?.setShowModulesSheet(true);
      plan?.setShowComments(true);
    });
  }, [navigation, section, plan]);

  const goBackAndShowSheet = React.useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return (
    <Screen noPadding>
      <BackToPlanHeader title="Learn" />

      {dataBoxes.map((box, i) => (
        <LearnBox key={i} {...box} />
      ))}

      <Button
        style={{ marginTop: 16 }}
        onPress={goBackAndShowSheet}
        icon={ThumbsUp}
      >
        Great! Take me back
      </Button>
    </Screen>
  );
});
